import type { RegionId, ScopeId, WorkType } from "@/types/mvpEstimate";

/** Strefa cenowa — korekta stawek, nie województwo administracyjne. */
export const WIZARD_REGION_OPTIONS: { id: RegionId; label: string; hint: string }[] = [
  {
    id: "mazowieckie",
    label: "Strefa najwyższych stawek",
    hint: "Wyższe koszty robocizny na rynkach z wyższymi stawkami",
  },
  {
    id: "aglomeracja",
    label: "Duże miasta",
    hint: "Stawki powyżej średniej krajowej",
  },
  {
    id: "regionalne",
    label: "Typowe miasta",
    hint: "Bazowy poziom cen w kosztorysie",
  },
  {
    id: "inne",
    label: "Miejscowości mniejsze",
    hint: "Zwykle niższe stawki niż w dużych ośrodkach",
  },
];

/** Pakiet w kreatorze — widoczna etykieta; `mapsToScopes` = dźwignie generatora. */
export type WizardScopeOption = {
  packageId: string;
  label: string;
  /**
   * Widoczny opis: co wchodzi / czego nie ma / co dopiszesz w tabeli (krótko, może być wielolinijkowy).
   */
  helper: string;
  /** Opcjonalnie: dodatkowy kontekst w tooltip przy „i”. */
  detailHint?: string;
  searchText: string;
  mapsToScopes: ScopeId[];
};

export type WizardScopeFamily = {
  id: string;
  label: string;
  /** Tooltip przy tytule — granice grupy. */
  familyDetailHint?: string;
  options: WizardScopeOption[];
};

/** Grupy wizualne w kroku zakresu (kolejność wyświetlania). */
export const WIZARD_SCOPE_SECTIONS: { id: string; title: string; familyIds: string[] }[] = [
  {
    id: "malowanie",
    title: "Malowanie",
    familyIds: ["malarskie-sciany", "malarskie-sciany-tylko", "malarskie-sufit-only"],
  },
  {
    id: "plytki-ceramiczne",
    title: "Płytki ceramiczne / gres",
    familyIds: ["plytki-ceramiczne"],
  },
  { id: "podlogi", title: "Podłogi", familyIds: ["podlogi"] },
  { id: "instalacje", title: "Instalacje", familyIds: ["hydraulika", "elektryka"] },
  { id: "drobne", title: "Drobne roboty", familyIds: ["naprawy", "montaze"] },
];

/** Kolejność `scopeIds` (stabilna). */
export const WIZARD_SCOPE_ORDER: ScopeId[] = [
  "sciany",
  "sciany_tylko",
  "sufit",
  "podloga",
  "plytki_ceramiczne",
  "hydraulika",
  "elektryka",
  "naprawy",
  "montaze",
];

/**
 * Ukryty pakiet (legacy + widoczny wariant): sufit bez pakietu ścian — `ScopeId` `sufit`.
 */
export const HIDDEN_LEGACY_PACKAGE_ID = "pkg-sufit-osobno" as const;

export const PKG_SCIANY_TYLO = "pkg-sciany-tylko" as const;
export const PKG_PLYTKI_CERAMICZNE = "pkg-plytki-ceramiczne" as const;
export const PKG_NAPRAWY = "pkg-naprawy" as const;
export const PKG_MONTAZE = "pkg-montaze" as const;

/**
 * Stare id pakietów z wcześniejszej wersji kreatora — nadal mapujemy do scope’ów
 * przy wczytywaniu; zapis normalizuje do kanonicznych `pkg-*`.
 */
export const DEPRECATED_PACKAGE_TO_SCOPES: Record<string, ScopeId[]> = {
  "pkg-gladzie-przygotowanie": ["sciany"],
  "pkg-zabudowy-gk": ["sciany"],
  "pkg-drzwi-wewnetrzne": ["sciany"],
  "pkg-listwy-parapety-montaze": ["podloga"],
  "pkg-podlogi-panele": ["podloga"],
  /** Wcześniej błędnie łączone z panelem — teraz osobny zakres generatora (płytki ceramiczne). */
  "pkg-podlogi-plytki-gres": ["plytki_ceramiczne"],
  "pkg-podlogi-cyklinowanie": ["podloga"],
  "pkg-elektryka-punkty-osprzet": ["elektryka"],
  "pkg-elektryka-przerobki": ["elektryka"],
  "pkg-hydraulika-bialy-montaz": ["hydraulika"],
  "pkg-hydraulika-przerobki": ["hydraulika"],
  [HIDDEN_LEGACY_PACKAGE_ID]: ["sufit"],
};

const H_PAINT = (extra: string) =>
  [
    "Obejmuje: typowe przygotowanie pod farbę i malowanie w relacji do m² (szacunek, nie oględziny).",
    "Bez: pełnej gładzi gipsowej, tapet, zabudów GK, wymiany drzwi — jeśli to Twój zakres, dopiszesz w tabeli.",
    `W tabeli: kolory ścian vs biały sufit, liczba warstw, ${extra}`,
  ].join("\n");

export const WIZARD_SCOPE_FAMILIES: WizardScopeFamily[] = [
  {
    id: "malarskie-sciany",
    label: "Ściany i sufit — razem",
    familyDetailHint:
      "Najczęstszy wariant: ściany kolor / sufit biały liczone w jednym szacunku. Gzymsy i ościeżnice doprecyzujesz w kosztorysie — tu tylko włączasz paczkę.",
    options: [
      {
        packageId: "pkg-malowanie",
        label: "Ściany + sufit (jedna paczka)",
        helper: H_PAINT("detale przy listwach i futrynach."),
        detailHint:
          "Nie rozdzielamy ścian i sufitu na osobne przedmiary — to celowo prostszy start niż opis pokój po pokoju.",
        searchText: "malowanie farba ściany sufit gładź przygotowanie grunt zabezpieczenie folia",
        mapsToScopes: ["sciany"],
      },
    ],
  },
  {
    id: "malarskie-sciany-tylko",
    label: "Tylko ściany",
    familyDetailHint:
      "Gdy sufit zostaje bez zmian albo robi go inna ekipa. Możesz jednocześnie włączyć „Tylko sufit” — wtedy liczą się dwie osobne paczki m².",
    options: [
      {
        packageId: PKG_SCIANY_TYLO,
        label: "Malowanie samych ścian (bez sufitu w tej paczce)",
        helper: [
          "Obejmuje: zabezpieczenie, grunt, farba na ścianach wg m² ścian (heurystyka z metrażu).",
          "Bez: sufitu, pełnej gładzi systemowej, tapet.",
          "W tabeli: kolory, narożniki, poprawki po osprzęcie.",
        ].join("\n"),
        detailHint:
          "Osobna ścieżka kosztowa — bez linii sufitu. Razem z pełnym pakietem „ściany+sufit” zostaje ten pełny pakiet (bez podwójnego liczenia).",
        searchText: "ściany bez sufitu tylko ściany farba",
        mapsToScopes: ["sciany_tylko"],
      },
    ],
  },
  {
    id: "malarskie-sufit-only",
    label: "Tylko sufit",
    familyDetailHint:
      "Gdy ścian nie ruszasz. Jeśli zaznaczysz też pełny pakiet ścian, liczy się on (sufit jest tam uwzględniony).",
    options: [
      {
        packageId: HIDDEN_LEGACY_PACKAGE_ID,
        label: "Sufit — grunt i farba (bez ścian)",
        helper: [
          "Obejmuje: przygotowanie i farba na suficie wg m² (szacunek).",
          "Bez: ścian, zabudów, oświetlenia wpuszczonego.",
          "W tabeli: biel / farba, ewentualne poprawki po grzejnikach.",
        ].join("\n"),
        detailHint:
          "Ten sam zakres co wcześniejszy zapis „tylko sufit”. Gdy nie zaznaczysz pełnego pakietu ścian, liczy się osobno.",
        searchText: "sufit tylko sufit bez ścian wilgoć przemalowanie",
        mapsToScopes: ["sufit"],
      },
    ],
  },
  {
    id: "plytki-ceramiczne",
    label: "Posadzka na mokro",
    familyDetailHint:
      "Klejenie płytek / gresu na posadzce — osobno od paneli laminowanych. Ściany i inne płaszczyzny dopiszesz w tabeli.",
    options: [
      {
        packageId: PKG_PLYTKI_CERAMICZNE,
        label: "Płytki ceramiczne / gres (klej, fuga, przygotowanie)",
        helper: [
          "Obejmuje: szacunek pod typową posadzkę (m² z metrażu głównego) — przygotowanie, klejenie, fuga, hydroizolacja wg zakresu.",
          "Bez: płytek na ścianach jako osobnego przedmiaru — tam dopiszesz linie w kosztorysie.",
          "W tabeli: format płytki, wzór, dylatacje, progi, spadki w łazience.",
        ].join("\n"),
        detailHint:
          "To nie są panele podłogowe — generator daje osobne linie robocizny i materiału pod ceramikę, nie pod laminat.",
        searchText: "płytki gres ceramiczne klej fuga hydroizolacja posadzka łazienka",
        mapsToScopes: ["plytki_ceramiczne"],
      },
    ],
  },
  {
    id: "podlogi",
    label: "Podłogi",
    familyDetailHint:
      "Panele i obróbki w jednym przełączniku. Łazienka na mokro lub kamień — dopiszesz jako osobne linie.",
    options: [
      {
        packageId: "pkg-podlogi",
        label: "Panele z przygotowaniem i listwami",
        helper: [
          "Obejmuje: zbiorczo przygotowanie, panele, listwy (wg m² i obwodu orientacyjnie).",
          "Bez: indywidualnego przedmiaru każdej listwy i progu — to dopiero w tabeli.",
          "W tabeli: typ paneli, progi, detale przy drzwiach.",
        ].join("\n"),
        searchText: "podłoga panele płytki gres cyklinowanie parkiet listwy progi przygotowanie",
        mapsToScopes: ["podloga"],
      },
    ],
  },
  {
    id: "hydraulika",
    label: "Woda i kanalizacja",
    familyDetailHint: "Punkty i rozprowadzenie od metrażu — nie projekt techniczny ani urządzenia kotłowni.",
    options: [
      {
        packageId: "pkg-hydraulika",
        label: "Hydraulika — punkty i montaż biały",
        helper: [
          "Obejmuje: orientacyjna liczba punktów i rozprowadzenie (wg m²).",
          "Bez: projektu, kotła, rekuperacji.",
          "W tabeli: dokładna liczba punktów, armatura, podłączenia.",
        ].join("\n"),
        searchText: "hydraulika woda kanalizacja baterie rury montaż",
        mapsToScopes: ["hydraulika"],
      },
    ],
  },
  {
    id: "elektryka",
    label: "Elektryka",
    familyDetailHint: "Punkty i obwody w relacji do metrażu — nie projekt elektryczny.",
    options: [
      {
        packageId: "pkg-elektryka",
        label: "Elektryka — obwody i gniazda",
        helper: [
          "Obejmuje: szacunek punktów i rozprowadzenia (wg m²).",
          "Bez: projektu, rozdzielnicy „pod klucz”, inteligentnego domu.",
          "W tabeli: liczba obwodów, osprzęt, RCD.",
        ].join("\n"),
        searchText: "elektryka prąd gniazda rozdzielnica kable puszki obwody",
        mapsToScopes: ["elektryka"],
      },
    ],
  },
  {
    id: "naprawy",
    label: "Drobne naprawy i przygotowanie",
    familyDetailHint:
      "Łatanie, szpachlowanie punktowe, przygotowanie pod wykończenie — szacunek zgrubny, bez udawania przedmiaru ubytków.",
    options: [
      {
        packageId: PKG_NAPRAWY,
        label: "Naprawy podłoża i przygotowanie",
        helper: [
          "Obejmuje: orientacyjny koszt drobnych uzupełnień i przygotowania pod farby / kleje (kpl.).",
          "Bez: pełnej rekonstrukcji ścian, wilgoci pod kontrolą inwestora.",
          "W tabeli: dokładny zakres ubytków, materiały, powtórne wizyty.",
        ].join("\n"),
        searchText: "łatanie szpachlowanie naprawy przygotowanie ubytki",
        mapsToScopes: ["naprawy"],
      },
    ],
  },
  {
    id: "montaze",
    label: "Drobne montaże i wykończenia",
    familyDetailHint:
      "Ościeżnice, listwy przydasowe, drobne montaże — start pod dopisanie sztuk i metrów w tabeli.",
    options: [
      {
        packageId: PKG_MONTAZE,
        label: "Montaże wykończeniowe (drobne)",
        helper: [
          "Obejmuje: orientacyjny koszt drobnych montaży i detali (kpl.).",
          "Bez: stolarzy meblowych, dużych okuć specjalnych.",
          "W tabeli: listwy, progi, klamki, ościeżnice — co realnie wchodzi.",
        ].join("\n"),
        searchText: "montaż listwy ościeżnice progi detale",
        mapsToScopes: ["montaze"],
      },
    ],
  },
];

const TYNKI_WORK_TYPE_KEYS = [
  "obrzutka_cementowa",
  "tynk_gipsowy_maszynowy",
  "tynk_cementowo_wapienny",
  "gruntowanie_tynk",
  "naroznik_aluminiowy",
  "obsadzenie_gniazdka",
  "zabezpieczenie_folia",
  "listwa_przyokienna",
  "tynkowanie_osciezy",
] as const;

const GLADZIE_WORK_TYPE_KEYS = [
  "gladzie",
  "gruntowanie_scian",
  "gruntowanie_sufitu",
] as const;

const ELEWACJA_WORK_TYPE_KEYS = [
  "tynk_elewacyjny_akryl",
  "tynk_elewacyjny_silikon",
  "tynk_mozaikowy_cokol",
  "boniowanie_mb",
  "podbitka_pcv",
  "podbitka_drewniana",
  "gzyms_listwa",
  "malowanie_elewacji",
  "obrobka_okna_osciezе",
] as const;

const WYLEWKI_WORK_TYPE_KEYS = [
  "wylewka_cementowa",
  "wylewka_anhydrytowa",
  "wylewka_doplata_cm",
  "wylewka_samopoziomujaca",
  "gruntowanie_podloga",
  "folia_pe",
  "tasma_dylatacyjna",
] as const;

/** Które `family.id` z `WIZARD_SCOPE_FAMILIES` są widoczne dla danego typu prac (krok 0). */
export const WORK_TYPE_VISIBLE_FAMILY_IDS: Record<WorkType, readonly string[]> = {
  malowanie: ["malarskie-sciany", "malarskie-sciany-tylko", "malarskie-sufit-only"],
  gladzie: GLADZIE_WORK_TYPE_KEYS,
  tynki: TYNKI_WORK_TYPE_KEYS,
  elewacja: ELEWACJA_WORK_TYPE_KEYS,
  wylewki: WYLEWKI_WORK_TYPE_KEYS,
  plytki: ["plytki-ceramiczne"],
  podlogi: ["podlogi"],
  zabudowy_gk: ["montaze"],
  /** Zakres jak scopeIds: hydraulika + montaze (bez elektryki). */
  bialy_montaz: ["hydraulika", "montaze"],
};

export function visibleFamilyIdsForWorkType(workType: WorkType): Set<string> {
  const v = WORK_TYPE_VISIBLE_FAMILY_IDS[workType];
  return new Set(v);
}

/** Sekcje kroku „Zakres prac” po filtrze typu prac (puste sekcje pomijane). */
export function wizardSectionsFilteredForWorkType(
  workType: WorkType
): { id: string; title: string; familyIds: string[] }[] {
  const allowed = visibleFamilyIdsForWorkType(workType);
  return WIZARD_SCOPE_SECTIONS.map((section) => ({
    ...section,
    familyIds: section.familyIds.filter((id) => allowed.has(id)),
  })).filter((s) => s.familyIds.length > 0);
}

const FAMILY_BY_ID: Record<string, WizardScopeFamily> = Object.fromEntries(
  WIZARD_SCOPE_FAMILIES.map((f) => [f.id, f])
);

export function wizardFamiliesForSection(
  section: (typeof WIZARD_SCOPE_SECTIONS)[number]
): WizardScopeFamily[] {
  return section.familyIds.map((id) => FAMILY_BY_ID[id]).filter(Boolean);
}

/** Wszystkie rozpoznawalne id pakietów (widoczne + ukryte legacy + deprecated — do parsowania zapisów). */
export const WIZARD_PACKAGE_IDS: Set<string> = new Set([
  ...WIZARD_SCOPE_FAMILIES.flatMap((f) => f.options.map((o) => o.packageId)),
  ...Object.keys(DEPRECATED_PACKAGE_TO_SCOPES),
]);

/** Stare id checkboxów → ScopeId (pojedynczy). */
export const LEGACY_SCOPE_ITEM_TO_SCOPE: Record<string, ScopeId> = {
  "malowanie-pakiet": "sciany",
  gladzie: "sciany",
  "sufit-osobno": "sufit",
  panele: "podloga",
  "plytki-gres": "plytki_ceramiczne",
  cyklinowanie: "podloga",
  "hydraulika-mniejszy": "hydraulika",
  "hydraulika-wiekszy": "hydraulika",
  "elektryka-prostsza": "elektryka",
  "elektryka-wiekszy": "elektryka",
};

export function scopesFromPackageId(id: string): ScopeId[] | null {
  for (const family of WIZARD_SCOPE_FAMILIES) {
    const opt = family.options.find((o) => o.packageId === id);
    if (opt) return opt.mapsToScopes;
  }
  const dep = DEPRECATED_PACKAGE_TO_SCOPES[id];
  if (dep) return dep;
  return null;
}

function parseSelectionToScopeSet(selectedItemIds: string[]): Set<ScopeId> {
  const set = new Set<ScopeId>();
  for (const id of selectedItemIds) {
    if (WIZARD_SCOPE_ORDER.includes(id as ScopeId)) {
      set.add(id as ScopeId);
      continue;
    }
    const fromPkg = scopesFromPackageId(id);
    if (fromPkg) {
      for (const s of fromPkg) set.add(s);
      continue;
    }
    const mapped = LEGACY_SCOPE_ITEM_TO_SCOPE[id];
    if (mapped) set.add(mapped);
  }
  return set;
}

/** Unikalne ScopeId z zaznaczenia (pakiety + legacy + surowe scopeIds). */
export function computeScopeIdsFromSelectedItems(selectedItemIds: string[]): ScopeId[] {
  const set = parseSelectionToScopeSet(selectedItemIds);
  return WIZARD_SCOPE_ORDER.filter((s) => set.has(s));
}

/**
 * Kanoniczne pakiety po `scopeIds`.
 * Pełne „ściany+sufit” wygrywa z „tylko ściany” przy tym samym zaznaczeniu.
 */
export function defaultPackagesFromScopeIds(scopeIds: ScopeId[]): string[] {
  const s = new Set(scopeIds);
  const out: string[] = [];
  if (s.has("sciany")) out.push("pkg-malowanie");
  else if (s.has("sciany_tylko")) out.push(PKG_SCIANY_TYLO);
  if (s.has("sufit") && !s.has("sciany")) out.push(HIDDEN_LEGACY_PACKAGE_ID);
  if (s.has("podloga")) out.push("pkg-podlogi");
  if (s.has("plytki_ceramiczne")) out.push(PKG_PLYTKI_CERAMICZNE);
  if (s.has("hydraulika")) out.push("pkg-hydraulika");
  if (s.has("elektryka")) out.push("pkg-elektryka");
  if (s.has("naprawy")) out.push(PKG_NAPRAWY);
  if (s.has("montaze")) out.push(PKG_MONTAZE);
  return out;
}

/**
 * Z zapisu (dowolne stare pkg / legacy / surowe scope) → kanoniczna lista pakietów.
 */
export function normalizeSelectionToPackages(selectedItemIds: string[]): string[] {
  const scopeIds = computeScopeIdsFromSelectedItems(selectedItemIds);
  if (scopeIds.length === 0) return [];
  return defaultPackagesFromScopeIds(scopeIds);
}

/** Zapis pomocniczy: tylko scopeIds — odwzoruj na pakiety domyślne. */
export function inferSelectedItemsFromScopeIds(scopeIds: ScopeId[]): string[] {
  return defaultPackagesFromScopeIds(scopeIds);
}

/** Czy zapis zawiera wyłącznie „tylko sufit” (np. migracja bez widocznego pakietu ścian). */
export function hasHiddenSufitOnlySelection(selectedScopeItemIds: string[]): boolean {
  return (
    selectedScopeItemIds.includes(HIDDEN_LEGACY_PACKAGE_ID) &&
    !selectedScopeItemIds.includes("pkg-malowanie")
  );
}
